/**
 * <SendTutorMessageModal> — parent → tutor message shell (template, L0.1).
 *
 * Mirrors legacy `SendTutorMessageModal`: title with tutor name, optional parent-portal hint,
 * multiline message field, `<MediaPicker>` attachments, submit footer. Effects (pickers, vapor
 * upload, API) are injected by the Screen via `mediaProps` + `onSubmit`.
 *
 * L'en-tête est celui des autres gestes (`<Modal steps>`) : le titre, la croix à droite. Ouvert
 * après « Quel tuteur ? » (`<TutorPickerModal>`), il en est la 2ᵉ étape — le retour à gauche, la
 * barre pleine. Ouvert sur un tuteur déjà connu, il est seul : ni retour, ni barre.
 */
import React from "react"

import { Banner } from "../../atoms/Banner"
import { Button } from "../../atoms/Buttons/Button"
import { TextInput } from "../../atoms/TextInput"
import { Stack } from "../../atoms/containers/Stack"
import { Text } from "../../atoms/typography/Text"
import { Modal } from "../../molecules/Modal"
import { MediaPicker, type MediaPickerProps } from "../../organisms/MediaPicker"

export type SendTutorMessageModalProps = {
    visible: boolean
    onClose: () => void
    tutorDisplayName: string
    message: string
    onMessageChange: (next: string) => void
    mediaProps: MediaPickerProps
    onSubmit: () => void
    submitLoading?: boolean
    submitDisabled?: boolean
    /** Parent-portal info banner under the title. */
    showParentHint?: boolean
    /** Hide without closing (camera / Genius Scan). */
    hidden?: boolean
    errorMessage?: string | null
    /**
     * Le retour vers « Quel tuteur ? ». Présent : le composeur est la 2ᵉ étape du parcours.
     * Absent : il est seul, le tuteur était connu.
     */
    onBack?: () => void
    testID?: string
}

export function SendTutorMessageModal(props: SendTutorMessageModalProps) {
    const {
        visible,
        onClose,
        tutorDisplayName,
        message,
        onMessageChange,
        mediaProps,
        onSubmit,
        submitLoading = false,
        submitDisabled = false,
        showParentHint = false,
        hidden = false,
        errorMessage,
        onBack,
        testID,
    } = props

    return (
        <Modal
            visible={visible}
            onClose={onClose}
            title={`Écrire à ${tutorDisplayName}`}
            steps={onBack != null ? { step: 2, total: 2, onBack } : { step: 1, total: 1 }}
            fullScreenOnSmall
            hidden={hidden}
            dismissable={!submitLoading}
            footer={
                <Button
                    label="Envoyer"
                    onPress={onSubmit}
                    loading={submitLoading}
                    disabled={submitDisabled || submitLoading}
                    fullWidth
                />
            }
            testID={testID}
        >
            <Stack gap="4">
                {showParentHint && (
                    <Banner
                        message="Les messages envoyés au tuteur sont visibles par l'élève."
                        icon="info-circle"
                        color="primary"
                        tinted
                    />
                )}
                <TextInput
                    label="Message"
                    placeholder="Message…"
                    value={message}
                    onChangeText={onMessageChange}
                    multiline
                />
                <Stack gap="2">
                    <Text weight="semibold">Pièces jointes</Text>
                    <MediaPicker {...mediaProps} enableExternalLink={false} multiple buttonLabel="Photo ou document" />
                </Stack>
                {errorMessage != null && errorMessage !== "" && (
                    <Banner message={errorMessage} color="danger" icon="exclamation-circle" tinted />
                )}
            </Stack>
        </Modal>
    )
}
